import { useQuoteDocumentEditable, useQuoteDocumentMode } from '@/components/quote-document/QuoteDocument';
import { useQuotePublicView } from '@/components/quote-document/QuotePublicView';
import { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import BlockRenderer from '@/components/BlockRenderer';
import { NODE_TO_BLOCK_TYPE } from '@/editor/blockConversion';
import { getVariablesMap } from '@/editor/extensions/variableNode';
import type { Block, BlockType } from '@/types';
import { BlockType as BT } from '@/types';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import TeamEditorPopover from '@/components/editor/TeamEditorPopover';
import { useBoxSignStatus } from '@/store/editorContext';
import { propagateClientLogo } from './logoSync';
import { uboTheme } from '@/theme/uboTheme';

const FULL_PAGE_BLOCK_TYPES = new Set<BlockType>([
  BT.COVER,
  BT.TEAM,
  // Afbeeldingsblokken zijn zelfstandige, randvullende slides.
  BT.IMAGE,
  BT.FOOTER,
]);

export default function BlockNodeView({ node, editor, updateAttributes, selected }: NodeViewProps) {
  const isEditable = useQuoteDocumentMode() === 'edit';
  const canEditText = useQuoteDocumentEditable();
  const attrs = node.attrs as Record<string, unknown>;
  const nodeName = (node.type as { name?: string })?.name ?? '';
  const blockType = nodeName ? (NODE_TO_BLOCK_TYPE[nodeName] as BlockType | undefined) : undefined;

  const editorBoxSignStatus = useBoxSignStatus();
  const publicView = useQuotePublicView();
  const boxSignStatus = publicView ? publicView.boxSignStatus : editorBoxSignStatus;

  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const handler = ({ transaction }: { transaction: { getMeta: (key: string) => unknown } }) => {
      if (transaction.getMeta('variablesMapUpdated')) {
        forceUpdate((n) => n + 1);
      }
    };
    editor.on('transaction', handler);
    return () => {
      editor.off('transaction', handler);
    };
  }, [editor]);

  const variableMap = getVariablesMap(editor);

  if (!blockType) return null;

  const block: Block = {
    id: (attrs.id as string) ?? '',
    quoteId: (attrs.quoteId as string) ?? '',
    type: blockType,
    order: (attrs.order as number) ?? 0,
    content: (attrs.content as Block['content']) ?? {},
    styles: (attrs.styles as Block['styles']) ?? {},
    config: (attrs.config as Block['config']) ?? {},
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const onUpdate = (patch: Partial<Block>) => {
    const updates: Record<string, unknown> = {};
    if (patch.content !== undefined) updates.content = patch.content;
    if (patch.styles !== undefined) updates.styles = patch.styles;
    if (patch.config !== undefined) updates.config = patch.config;
    if (Object.keys(updates).length > 0) updateAttributes(updates);

    const newLogo = (patch.content as Record<string, unknown> | undefined)?.clientLogoUrl as
      | string
      | undefined;
    if (newLogo) {
      propagateClientLogo(editor, node, newLogo);
    }
  };

  // De handtekeningpagina is geen vaste pagina: hij loopt door naar een volgend vel als
  // voorwaarden en tekenvak niet op één vel passen, in de editor net als voor de klant.
  const isFullPage = FULL_PAGE_BLOCK_TYPES.has(blockType);
  const isTeam = blockType === BT.TEAM;
  const blockContent = block.content as Record<string, unknown>;
  const fullBleedTheme = blockContent.theme === 'ubx' ? 'ubx' : 'ubo';
  const fullBleedBackground =
    blockType === BT.COVER
      ? (blockContent.backgroundUrl as string | undefined)
      : blockType === BT.IMAGE
        ? (blockContent.url as string | undefined)
      : blockType === BT.FOOTER
        ? fullBleedTheme === 'ubx'
          ? '/ubo/ubx-background-1.svg'
          : '/ubo/background-1.svg'
        : undefined;
  const fullBleedColor =
    blockType === BT.TEAM
      ? fullBleedTheme === 'ubx'
        ? uboTheme.colors.ubx.primary
        : uboTheme.colors.secondary
      : blockType === BT.COVER || blockType === BT.FOOTER
      ? fullBleedTheme === 'ubx'
        ? uboTheme.colors.ubx.primary
        : uboTheme.colors.primary
      : undefined;

  const renderer = (
    <BlockRenderer
      block={block}
      isEditable={canEditText && !isTeam}
      onUpdate={onUpdate}
      variableMap={variableMap}
      boxSignStatus={boxSignStatus}
      {...(publicView
        ? {
            isPublic: true,
            quoteStatus: publicView.quoteStatus,
            publicLocale: publicView.publicLocale,
            boxSignedAt: publicView.boxSignedAt,
            onSignature: publicView.onSignature,
            existingSignature: publicView.existingSignature,
            onAccept: publicView.onAccept,
            acceptLoading: publicView.acceptLoading,
            onSignAsRecipient: publicView.onSignAsRecipient,
            signingRecipientId: publicView.signingRecipientId,
          }
        : {})}
    />
  );

  return (
    <NodeViewWrapper
      as="div"
      className={`block-node-view relative ${selected ? 'is-selected' : ''}`}
      data-block-id={block.id}
      data-full-page={isFullPage || undefined}
      data-full-bleed-image={blockType === BT.IMAGE || undefined}
      data-full-bleed-background={fullBleedBackground ? true : undefined}
      style={
        isFullPage
          ? {
              height: A4_LANDSCAPE.contentHeight,
              overflow: 'clip',
              backgroundColor: fullBleedColor,
              backgroundImage: fullBleedBackground ? `url(${fullBleedBackground})` : undefined,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }
          : undefined
      }
    >
      {isFullPage ? (
        /*
         * Een volle pagina is precies één vel: de inhoud vult de bloklaag als
         * absolute laag en de paginering verdeelt hem niet (data-full-page).
         */
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>{renderer}</div>
      ) : (
        renderer
      )}
      {isEditable && isTeam && <TeamEditorPopover block={block} onUpdate={onUpdate} />}
    </NodeViewWrapper>
  );
}
